import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  User,
  Shield,
  KeyRound,
  FolderTree,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Bike,
  ShoppingBag,
  Briefcase,
  Phone,
  Mail,
  Calendar,
  LogIn,
  ShieldOff,
  ShieldCheck,
  Plus,
  Trash2,
  Ban,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@lib/utils';
import { usersService } from '@services/users.service';
import { rolesService } from '@services/roles.service';
import { departmentsService } from '@services/departments.service';
import { permissionsService } from '@services/permissions.service';
import type { UserSummaryResponse } from '@apptypes/index';
import { PermissionGate } from '@components/PermissionGate';
import { usePermission } from '@hooks/usePermission';

interface Props {
  user: UserSummaryResponse;
  onClose: () => void;
}

type Tab = 'overview' | 'departments' | 'roles' | 'permissions';

const statusColors: Record<string, string> = {
  active:               'bg-success-100 text-success-700',
  suspended:            'bg-danger-100 text-danger-700',
  deactivated:          'bg-gray-100 text-gray-500',
  pending_verification: 'bg-warning-100 text-warning-700',
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDateOnly(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ user, onClose }: { user: UserSummaryResponse; onClose: () => void }) {
  const queryClient = useQueryClient();

  // Re-fetch the full detail (includes superAdmin flag) when the modal opens
  const { data: detail } = useQuery({
    queryKey: ['admin-user-detail', user.userId],
    queryFn: () => usersService.get(user.userId),
    initialData: user,
  });

  const isSuperAdmin = detail?.superAdmin === true;

  const suspendMutation = useMutation({
    mutationFn: () => usersService.suspend(user.userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => usersService.activate(user.userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
  });

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500 w-40 shrink-0">{label}</span>
      <span className="text-sm text-gray-900 text-right">{value ?? '—'}</span>
    </div>
  );

  const VerifiedBadge = ({ verified }: { verified?: boolean }) =>
    verified ? (
      <span className="inline-flex items-center gap-1 text-success-600 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Verified</span>
    ) : (
      <span className="inline-flex items-center gap-1 text-gray-400 text-xs"><XCircle className="w-3.5 h-3.5" /> Not verified</span>
    );

  return (
    <div className="space-y-6">
      {isSuperAdmin && (
        <div className="flex items-center gap-2 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg text-sm text-primary-700">
          <Shield className="w-4 h-4 shrink-0" />
          <span>This user is a <strong>super admin</strong>. Suspension and role removal are blocked.</span>
        </div>
      )}

      {/* Identity */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Identity</h3>
        <div className="bg-gray-50 rounded-lg px-4">
          <InfoRow label="User ID" value={<code className="text-xs text-gray-600 font-mono">{user.userId}</code>} />
          <InfoRow label="Phone" value={
            <span className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-gray-400" />{user.phone}
              <VerifiedBadge verified={user.phoneVerified} />
            </span>
          } />
          <InfoRow label="Email" value={
            user.email ? (
              <span className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-gray-400" />{user.email}
                <VerifiedBadge verified={user.emailVerified} />
              </span>
            ) : <span className="text-gray-400 text-xs">Not set</span>
          } />
        </div>
      </div>

      {/* Status */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Status</h3>
        <div className="bg-gray-50 rounded-lg px-4">
          <InfoRow label="Account status" value={
            <span className={cn('px-2 py-0.5 rounded-full text-xs font-semibold', statusColors[user.status] ?? 'bg-gray-100')}>
              {user.status.replace('_', ' ')}
            </span>
          } />
          {user.suspendedAt && (
            <InfoRow label="Suspended at" value={formatDate(user.suspendedAt)} />
          )}
          {user.suspensionReason && (
            <InfoRow label="Suspension reason" value={
              <span className="text-danger-600 text-xs">{user.suspensionReason}</span>
            } />
          )}
        </div>
      </div>

      {/* User type flags */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">User Types</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { flag: user.cheKitStaff,  label: 'Chekit Staff',   icon: Briefcase,   color: 'bg-primary-100 text-primary-700' },
            { flag: user.businessUser, label: 'Business User',  icon: Building2,   color: 'bg-[var(--bg-elevated)] text-[var(--text-accent)]' },
            { flag: user.customer,     label: 'Customer',       icon: ShoppingBag, color: 'bg-green-100 text-green-700' },
            { flag: user.rider,        label: 'Rider',          icon: Bike,        color: 'bg-orange-100 text-orange-700' },
          ].map(({ flag, label, icon: Icon, color }) => (
            <span key={label} className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium',
              flag ? color : 'bg-gray-100 text-gray-400 line-through')}>
              <Icon className="w-3.5 h-3.5" />{label}
            </span>
          ))}
        </div>
      </div>

      {/* Activity */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Activity</h3>
        <div className="bg-gray-50 rounded-lg px-4">
          <InfoRow label="Joined" value={
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />{formatDateOnly(user.createdAt)}
            </span>
          } />
          <InfoRow label="Last login" value={
            <span className="flex items-center gap-1.5">
              <LogIn className="w-3.5 h-3.5 text-gray-400" />{formatDate(user.lastLoginAt)}
            </span>
          } />
          <InfoRow label="Login count" value={
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-gray-400" />{user.loginCount ?? 0}
            </span>
          } />
          <InfoRow label="Failed attempts" value={
            <span className={cn('flex items-center gap-1.5', (user.failedLoginAttempts ?? 0) > 0 && 'text-warning-600')}>
              <AlertTriangle className="w-3.5 h-3.5" />{user.failedLoginAttempts ?? 0}
            </span>
          } />
        </div>
      </div>

      {/* Actions */}
      {!isSuperAdmin && (
        <div className="flex gap-3 pt-2">
          {user.status === 'active' && (
            <PermissionGate permission="users.suspend">
              <button
                onClick={() => suspendMutation.mutate()}
                disabled={suspendMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-danger-50 text-danger-700 border border-danger-200 rounded-lg text-sm font-medium hover:bg-danger-100 transition-colors"
              >
                <ShieldOff className="w-4 h-4" /> Suspend User
              </button>
            </PermissionGate>
          )}
          {user.status === 'suspended' && (
            <PermissionGate permission="users.activate">
              <button
                onClick={() => activateMutation.mutate()}
                disabled={activateMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-success-50 text-success-700 border border-success-200 rounded-lg text-sm font-medium hover:bg-success-100 transition-colors"
              >
                <ShieldCheck className="w-4 h-4" /> Activate User
              </button>
            </PermissionGate>
          )}
        </div>
      )}
    </div>
  );
}

// ── Departments Tab ───────────────────────────────────────────────────────────

function DepartmentsTab({ userId }: { userId: string }) {
  const [showAdd, setShowAdd] = useState(false);
  const queryClient = useQueryClient();

  const { data: userDepts = [], isLoading } = useQuery({
    queryKey: ['user-departments', userId],
    queryFn: () => usersService.getDepartments(userId),
  });

  const { data: allDepts = [], isLoading: loadingAll } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.list(),
    enabled: showAdd,
  });

  const addMutation = useMutation({
    mutationFn: (departmentId: string) => usersService.addToDepartment(userId, departmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-departments', userId] });
      setShowAdd(false);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (departmentId: string) => usersService.removeFromDepartment(userId, departmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-departments', userId] }),
  });

  const assignedIds = new Set(userDepts.map((d) => d.departmentId));
  const available = allDepts.filter((d) => !assignedIds.has(d.departmentId));

  if (isLoading) return <div className="p-8 text-center text-gray-500">Loading departments...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Chekit-level departments this user belongs to</p>
        <PermissionGate permission="departments.assign_members">
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-lg text-sm font-medium hover:bg-primary-100 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add to Department
          </button>
        </PermissionGate>
      </div>

      {showAdd && (
        <div className="border border-primary-200 rounded-lg p-3 bg-primary-50">
          {loadingAll ? (
            <p className="text-sm text-gray-500">Loading departments...</p>
          ) : available.length === 0 ? (
            <p className="text-sm text-gray-500">User is already in all available departments.</p>
          ) : (
            <div className="space-y-1">
              {available.map((dept) => (
                <button
                  key={dept.departmentId}
                  onClick={() => addMutation.mutate(dept.departmentId)}
                  disabled={addMutation.isPending}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-md border border-gray-200 hover:border-primary-400 text-sm transition-colors"
                >
                  <span className="font-medium text-gray-900">{dept.name}</span>
                  <span className="text-xs text-gray-400">{dept.permissions.length} permissions</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {userDepts.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">Not in any Chekit-level departments.</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {userDepts.map((dept) => (
            <div key={dept.departmentId} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <FolderTree className="w-4 h-4 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900">{dept.name}</div>
                  {dept.description && <div className="text-xs text-gray-500">{dept.description}</div>}
                  <div className="text-xs text-gray-400">{dept.permissions.length} permissions</div>
                </div>
              </div>
              <PermissionGate permission="departments.assign_members">
                <button
                  onClick={() => removeMutation.mutate(dept.departmentId)}
                  disabled={removeMutation.isPending}
                  className="p-1.5 text-gray-400 hover:text-danger-500 hover:bg-danger-50 rounded transition-colors"
                  title="Remove from department"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </PermissionGate>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Roles Tab ─────────────────────────────────────────────────────────────────

function RolesTab({ userId }: { userId: string }) {
  const [showAssign, setShowAssign] = useState(false);
  const queryClient = useQueryClient();

  const { data: userRoles = [], isLoading: loadingRoles } = useQuery({
    queryKey: ['user-roles', userId],
    queryFn: () => usersService.getRoles(userId),
  });

  const { data: allRoles = [], isLoading: loadingAll } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesService.list(),
    enabled: showAssign,
  });

  const assignMutation = useMutation({
    mutationFn: (roleId: string) => usersService.assignRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles', userId] });
      setShowAssign(false);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (roleId: string) => usersService.removeRole(userId, roleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-roles', userId] }),
  });

  const assignedIds = new Set(userRoles.map((r) => r.roleId));
  const unassigned = allRoles.filter((r) => !assignedIds.has(r.roleId));

  if (loadingRoles) return <div className="p-8 text-center text-gray-500">Loading roles...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Chekit-level roles assigned to this user</p>
        <PermissionGate permission="roles.assign">
          <button
            onClick={() => setShowAssign((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-lg text-sm font-medium hover:bg-primary-100 transition-colors"
          >
            <Plus className="w-4 h-4" /> Assign Role
          </button>
        </PermissionGate>
      </div>

      {showAssign && (
        <div className="border border-primary-200 rounded-lg p-3 bg-primary-50">
          {loadingAll ? (
            <p className="text-sm text-gray-500">Loading available roles...</p>
          ) : unassigned.length === 0 ? (
            <p className="text-sm text-gray-500">No unassigned roles available.</p>
          ) : (
            <div className="space-y-1">
              {unassigned.map((role) => (
                <button
                  key={role.roleId}
                  onClick={() => assignMutation.mutate(role.roleId)}
                  disabled={assignMutation.isPending}
                  className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-md border border-gray-200 hover:border-primary-400 text-sm transition-colors"
                >
                  <span className="font-medium text-gray-900">{role.name}</span>
                  {role.isSystemRole && (
                    <span className="text-xs px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded">System</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {userRoles.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">No Chekit-level roles assigned.</div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {userRoles.map((role) => (
            <div key={role.roleId} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50">
              <div className="flex items-center gap-3">
                <Shield className={cn('w-4 h-4', role.isSystemRole ? 'text-primary-500' : 'text-gray-400')} />
                <div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {role.name}
                    {role.isSystemRole && (
                      <span className="text-xs px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded">System</span>
                    )}
                  </div>
                  {role.description && <div className="text-xs text-gray-500">{role.description}</div>}
                </div>
              </div>
              {!role.isSystemRole && (
                <PermissionGate permission="roles.assign">
                  <button
                    onClick={() => removeMutation.mutate(role.roleId)}
                    disabled={removeMutation.isPending}
                    className="p-1.5 text-gray-400 hover:text-danger-500 hover:bg-danger-50 rounded transition-colors"
                    title="Remove role"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </PermissionGate>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Permissions Tab ───────────────────────────────────────────────────────────

function PermissionsTab({ userId }: { userId: string }) {
  const [showGrantPicker, setShowGrantPicker] = useState<'grant' | 'deny' | null>(null);
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();

  const { data: directPerms = [], isLoading: loadingDirect } = useQuery({
    queryKey: ['user-direct-permissions', userId],
    queryFn: () => usersService.getDirectPermissions(userId),
  });

  const { data: effective, isLoading: loadingEffective } = useQuery({
    queryKey: ['user-effective-permissions', userId],
    queryFn: () => usersService.getEffectivePermissions(userId),
  });

  const { data: allPerms = [], isLoading: loadingAll } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => permissionsService.list(),
    enabled: showGrantPicker !== null,
  });

  const grantMutation = useMutation({
    mutationFn: ({ permissionId, deny }: { permissionId: string; deny: boolean }) =>
      usersService.grantPermission(userId, permissionId, deny),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-direct-permissions', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-effective-permissions', userId] });
      setShowGrantPicker(null);
      setSearch('');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (permissionId: string) => usersService.revokeDirectPermission(userId, permissionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-direct-permissions', userId] });
      queryClient.invalidateQueries({ queryKey: ['user-effective-permissions', userId] });
    },
  });

  const directPermIds = new Set(directPerms.map((p) => p.permissionId));
  const filteredAvailable = allPerms
    .filter((p) => !directPermIds.has(p.permissionId))
    .filter((p) => !search || p.code.includes(search.toLowerCase()) || p.name.toLowerCase().includes(search.toLowerCase()));

  const effectivePerms = effective?.permissions ?? [];
  const grouped = effectivePerms.reduce<Record<string, string[]>>((acc, code) => {
    const mod = code.split('.')[0] ?? 'other';
    if (!acc[mod]) acc[mod] = [];
    acc[mod].push(code);
    return acc;
  }, {});

  return (
    <div className="space-y-6">

      {/* Direct grants/denies */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Direct Permission Overrides</h3>
          <PermissionGate permission="permissions.assign">
            <div className="flex gap-2">
              <button
                onClick={() => setShowGrantPicker(showGrantPicker === 'grant' ? null : 'grant')}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-success-50 text-success-700 border border-success-200 rounded-lg text-xs font-medium hover:bg-success-100 transition-colors"
              >
                <CheckCircle className="w-3.5 h-3.5" /> Grant
              </button>
              <button
                onClick={() => setShowGrantPicker(showGrantPicker === 'deny' ? null : 'deny')}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-danger-50 text-danger-700 border border-danger-200 rounded-lg text-xs font-medium hover:bg-danger-100 transition-colors"
              >
                <Ban className="w-3.5 h-3.5" /> Deny
              </button>
            </div>
          </PermissionGate>
        </div>

        <p className="text-xs text-gray-400 mb-3">
          Direct grants add a permission regardless of role/dept. Direct denies block a permission even if inherited. Deny always wins.
        </p>

        {showGrantPicker !== null && hasPermission('permissions.assign') && (
          <div className={cn(
            'border rounded-lg p-3 mb-3',
            showGrantPicker === 'grant' ? 'border-success-200 bg-success-50' : 'border-danger-200 bg-danger-50'
          )}>
            <div className="mb-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions..."
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
            </div>
            {loadingAll ? (
              <p className="text-sm text-gray-500">Loading permissions...</p>
            ) : filteredAvailable.length === 0 ? (
              <p className="text-sm text-gray-500">No permissions available to {showGrantPicker}.</p>
            ) : (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {filteredAvailable.map((perm) => (
                  <button
                    key={perm.permissionId}
                    onClick={() => grantMutation.mutate({ permissionId: perm.permissionId, deny: showGrantPicker === 'deny' })}
                    disabled={grantMutation.isPending}
                    className="w-full flex items-center justify-between px-3 py-2 bg-white rounded-md border border-gray-200 hover:border-primary-400 text-xs transition-colors text-left"
                  >
                    <span className="font-mono text-gray-700">{perm.code}</span>
                    <span className="text-gray-400 ml-2 shrink-0">{perm.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {loadingDirect ? (
          <div className="text-sm text-gray-500">Loading direct permissions...</div>
        ) : directPerms.length === 0 ? (
          <div className="py-4 text-center text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
            No direct permission overrides set.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {directPerms.map((dp) => (
              <div key={dp.userPermissionId} className="flex items-center justify-between px-4 py-2.5 bg-white hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold',
                    dp.grantType === 'grant' ? 'bg-success-100 text-success-700' : 'bg-danger-100 text-danger-700'
                  )}>
                    {dp.grantType === 'grant' ? <CheckCircle className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                    {dp.grantType}
                  </span>
                  <span className="text-xs font-mono text-gray-700">{dp.code}</span>
                  <span className="text-xs text-gray-400 hidden sm:inline">{dp.name}</span>
                </div>
                <PermissionGate permission="permissions.assign">
                  <button
                    onClick={() => revokeMutation.mutate(dp.permissionId)}
                    disabled={revokeMutation.isPending}
                    className="p-1.5 text-gray-400 hover:text-danger-500 hover:bg-danger-50 rounded transition-colors"
                    title="Revoke override"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </PermissionGate>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Effective permissions (resolved) */}
      <div>
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
          Effective Permissions ({effectivePerms.length} resolved)
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          All permissions this user currently has: from departments + roles + direct grants, minus any denies.
        </p>
        {loadingEffective ? (
          <div className="text-sm text-gray-500">Resolving permissions...</div>
        ) : effectivePerms.length === 0 ? (
          <div className="py-4 text-center text-gray-400 text-sm">No effective permissions.</div>
        ) : (
          <div className="space-y-2">
            {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([mod, codes]) => (
              <div key={mod} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">{mod}</span>
                  <span className="text-xs text-gray-400">{codes.length}</span>
                </div>
                <div className="px-4 py-2 flex flex-wrap gap-1.5">
                  {codes.map((code) => (
                    <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--bg-elevated)] text-[var(--text-accent)] text-xs font-mono rounded">
                      <KeyRound className="w-3 h-3" />
                      {code.split('.').slice(1).join('.')}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────

export function UserDetailModal({ user, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('overview');

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'overview',     label: 'Overview',     icon: User      },
    { id: 'departments',  label: 'Departments',  icon: FolderTree },
    { id: 'roles',        label: 'Roles',        icon: Shield    },
    { id: 'permissions',  label: 'Permissions',  icon: KeyRound  },
  ];

  const initials = `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)] backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white font-bold text-lg">
              {initials}
            </div>
            <div>
              <div className="font-bold text-gray-900 text-lg">{user.firstName} {user.lastName}</div>
              <div className="text-sm text-gray-500">{user.phone}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-2">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
                tab === id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'overview'    && <OverviewTab user={user} onClose={onClose} />}
          {tab === 'departments' && <DepartmentsTab userId={user.userId} />}
          {tab === 'roles'       && <RolesTab userId={user.userId} />}
          {tab === 'permissions' && <PermissionsTab userId={user.userId} />}
        </div>
      </div>
    </div>
  );
}
